import React, { useCallback, useEffect, useState } from "react";

import { addChapterToCourse, deleteChapter, getChaptersByCourse, updateChapter } from "services";

import { incomplete, useCancelAsyncOnUnmount } from "utils";

import { AdminSection, Button, Col, Divider, Form, Icon, Input, Row, Tooltip, Message as message } from "components";

import { ChapterDetails } from "pages/admin/content/components/ChapterDetails";

export const Chapters = ({ courseId }) => {
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(false);
  const signal = useCancelAsyncOnUnmount()();

  const [form] = Form.useForm();

  const loadChapters = useCallback(() => {
    if (!courseId) {
      return;
    }
    getChaptersByCourse({
      courseId,
      signal,
    })
      .then((res) => {
        setChapters(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [courseId, signal]);

  const chapterComplete = () => {
    setLoading(true);

    addChapterToCourse({
      title: "New Chapter",
      courseId,
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
        form.resetFields();
      })
      .then(() => {
        loadChapters();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateChapter = (title, chapterId) => {
    setLoading(true);

    updateChapter({
      chapterId,
      title,
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
        form.resetFields();
      })
      .then(() => {
        loadChapters();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const PanelExtra = ({ chapter }) => {
    const handleDelete = (e) => {
      e.stopPropagation();
      setLoading(true);

      deleteChapter({
        courseId,
        chapterId: chapter.id,
        signal,
      })
        .then(() => {
          message({
            content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
            icon: "gremlin",
          });
          loadChapters();
        })
        .finally(() => {
          setLoading(false);
        });
    };

    return (
      <div>
        {currentChapter !== chapter.id ? (
          <Tooltip placement="top" title="Edit this chapter">
            <Icon iconName="tabler/pencil" className="pointer green-text icon inline" onClick={() => setCurrentChapter(chapter.id)} />
          </Tooltip>
        ) : (
          <Tooltip placement="top" title="Cancel editing">
            <Icon iconName="tabler/x" className="pointer green-text icon inline" onClick={() => setCurrentChapter(null)} />
          </Tooltip>
        )}
        {!chapter.modules.length && currentChapter !== chapter.id && (
          <Tooltip placement="top" title="Delete this chapter">
            <Icon iconName="tabler/trash" className="pointer green-text icon inline" onClick={handleDelete} />
          </Tooltip>
        )}
      </div>
    );
  };

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  return (
    <>
      <AdminSection
        description={
          <>
            Add chapters to the course here. You cannot reorder chapters, and you must delete all moodules in a chapter before you can delete the chapter.{" "}
            <b>Data for videos is retrieved based on the video ID provided every hour - videos may not display or play until then.</b>
          </>
        }
      />
      <Row>
        <Col>
          <Form form={form} name="confirm" onFinish={chapterComplete} onFinishFailed={incomplete}>
            <Row>
              <Col sm={12} md={8}>
                <Button htmlType="submit" type="primary" disabled={loading} loading={loading}>
                  Add Chapter
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col>
          {chapters.map((chapter, i) => (
            <div className="margin-bottom-200" key={i}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "0 0 2rem",
                }}
              >
                {currentChapter === chapter.id ? (
                  <Form name="title" style={{ width: "60%", position: "relative" }}>
                    <Input
                      name="title"
                      label="Chapter title"
                      onBlur={(e) => handleUpdateChapter(e.target.value, chapter.id)}
                      rules={[{ required: true }]}
                      initialValue={chapter.title}
                    />
                  </Form>
                ) : (
                  <h5>{chapter.title}</h5>
                )}
                <PanelExtra chapter={chapter} />
              </div>
              {currentChapter === chapter.id ? (
                <ChapterDetails chapter={chapter} courseId={courseId} loading={loading} loadChapters={loadChapters} setLoading={setLoading} />
              ) : null}
              <Divider />
            </div>
          ))}
        </Col>
      </Row>
    </>
  );
};
